import {defineStore} from 'pinia'
import {NetworkService} from "~/utils/NetworkService";
import type {Fee} from "~/models/fee";
import type {GetPaidPaymentsByUserIdRb} from "~/models/request/getPaidPaymentsByUserIdRb";
import type {PaidPayment} from "~/models/paidPayment";
import type {GetFeesBySourceIdRb} from "~/models/request/getFeesBySourceIdRb";
import type {FeeIdAndQuantity} from "~/models/feeIdAndQuantity";
import type {AddUpdateFeeRb} from "~/models/request/addUpdateFeeRb"
import type {AssignFeeRb} from "~/models/request/assignFeeRb";
import type {updateCashPaymentRb} from "~/models/request/updateCashPaymentRb";
import type {AddUpdateClubFeeRb, ClubFee} from "~/models/request/addUpdateClubFeeRb";
import type {GetEligibleStudentsRb, GetEligibleStudentsResponse} from "~/models/request/clubFee/getEligibleStudentsRb";
import type {ConnectFeeToStudentsRb, ConnectFeeToStudentsResponse} from "~/models/request/clubFee/connectFeeToStudentsRb";
import type {GetEligibleFeesForStudentRb, GetEligibleFeesForStudentResponse} from "~/models/request/clubFee/getEligibleFeesForStudentRb";
import type {ConnectStudentToFeesRb, ConnectStudentToFeesResponse} from "~/models/request/clubFee/connectStudentToFeesRb";


export const feeStore = defineStore({
    id: 'fee-store',
    persist: true,
    state: () => {
        return {
            allFeesOfThisCompany: <Fee[]>[],
            fees: <Fee[]>[],
            paidPayments: <PaidPayment[]>[],
            selectedFees: <Fee[]>[],
            feesForEventRegistration: <Fee[]>[],
            feeIdAndQuantityListForEventRegistration: <FeeIdAndQuantity[]>[],
            feesForCreateEventForm: <Fee[]>[],
            localClubFeeRb: <ClubFee[]>[],
            allFeesOfThisClub: <ClubFee[]>[],
        }
    },
    actions: {
        clearFeeStore: function () {
            this.clearFees();
            this.clearSelectedFees();
            this.clearFeesForCreateEventForm();
        },
        clearEventRegistrationData: function () {
          this.clearFeesForEventRegistration();
          this.clearFeeIdAndQuantityListForEventRegistration();
        },
        clearFees: function () {
            this.fees = [];
        },
        clearSelectedFees: function () {
            this.selectedFees = [];
        },
        clearFeesForEventRegistration: function () {
          this.feesForEventRegistration = [];
        },
        clearFeeIdAndQuantityListForEventRegistration: function () {
            this.feeIdAndQuantityListForEventRegistration = [];
        },
        clearFeesForCreateEventForm: function () {
            this.feesForCreateEventForm =[];
        },
        clearLocalClubFeeRb: function () {
            this.localClubFeeRb =[];
        },

        // Return fees loaded of this source
        feesBySourceId: function (){
            const tmpFees = <Fee[]>[];
            for(const tmpFee of this.fees){
                tmpFees.push(tmpFee)
            }
            return tmpFees;
        },

        // Get fee by fee id
        getFeeByIdFromAllFeesOfTheCompany: function (feeId: number) {
          return this.allFeesOfThisCompany.find((fee)=>fee.id==feeId);
        },

        // Select/Unselect fee
        switchFeeSelection: function (fee:Fee){
            const foundFee = this.selectedFees.find((tmpFee)=>tmpFee.id==fee.id);
            if(foundFee){
                this.selectedFees = this.selectedFees.filter((tmpFee)=>tmpFee.id!=fee.id);
            }else{
                this.selectedFees.push(fee);
            }
        },

        // Return selected Fees
        getSelectedFees: function (){
            const tmpFees = <Fee[]>[];
            for(const tmpFee of this.selectedFees){
                tmpFees.push(tmpFee)
            }
            return tmpFees;
        },

        // Get total amount of selectedFees
        selectedFeesTotalAmount: function (studentsCount:number) {
            let amount:number = 0;
            for(const tmpFee of this.fees){
                amount = amount+tmpFee.feeAmount;
            }
            amount = amount*studentsCount;
            return Number(amount).toFixed(2);
        },

        // Load all fees of a company
        loadAllFeesOfACompany: async function (companyId:number) {
            this.allFeesOfThisCompany = [];
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/fee/getFeesByCompanyId';
            const ns = new NetworkService();
            //Type 2 is for club
            let {Result} = await ns.post$fetch(url,{"companyId": companyId},null, "getFeesByCompanyId" + new Date().getMilliseconds() + "_")
            if((Result?.length??-1)>0){
                for(const item of Result){
                    this.allFeesOfThisCompany.push(item as Fee);
                }
                return Result
            }
        },

        // Load all fees of a club
        loadAllFeesOfThisClub: async function (clubId:number) {
            this.allFeesOfThisClub = [];
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/club-fee/get-club-fees-by-club-id';
            const ns = new NetworkService();
            //Type 2 is for club
            let {Result} = await ns.post$fetch(url,{"clubId": clubId},null, "getFeesByClubId" + new Date().getMilliseconds() + "_")
            if((Result?.length ?? 0) > 0){
                for(const item of Result){
                    this.allFeesOfThisClub.push(item as ClubFee);
                }
            }
            return Result ?? [];
        },

        // Create or update club fees (batch operation)
        createOrUpdateClubFees: async function(clubIds: number[], fees: ClubFee[], companyId?: number) {
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/club-fee/create-or-update-fee';
            const ns = new NetworkService();

            // Prepare the request body
            const requestBody = {
                clubIds: clubIds,
                ...(companyId ? { companyId: companyId } : {}),
                fees: fees.map((fee, index) => ({
                    // Only include id if it's not a temporary ID (not starting with 'tmp_')
                    ...(fee.id && !String(fee.id).startsWith('tmp_') ? { id: fee.id } : {}),
                    name: fee.name,
                    description: fee.description || "",
                    amount: fee.amount,
                    type: fee.type,
                    created_by: fee.created_by,
                    allow_partial_payment: fee.allow_partial_payment,
                    min_partial_amount: fee.min_partial_amount,
                    is_required: fee.is_required,
                    enrollment_type: fee.enrollment_type,
                    enrollment_config: fee.enrollment_config,
                    mapping: {
                        // Only include mapping.id if it exists and fee.id exists and is not temporary
                        ...(fee.mapping?.id && fee.id && !String(fee.id).startsWith('tmp_') ? { id: fee.mapping.id } : {}),
                        recurring_schedule: fee.mapping.recurring_schedule,
                        available_quantity: fee.mapping.available_quantity,
                        start_date: fee.mapping.start_date,
                        end_date: fee.mapping.end_date,
                        is_active: fee.mapping.is_active,
                        sort_order: index + 1, // Index-based sort order (1-indexed)
                        config: fee.mapping.config,
                        status: fee.mapping.status
                    }
                }))
            };

            let {Result} = await ns.post$fetch(url, requestBody, null, "createOrUpdateClubFees" + new Date().getMilliseconds() + "_");
            return Result;
        },

        // Load all fees from network by source id
        loadAllFeesBySourceId: async function (sourceId:number, companyId:number, withSet:boolean|null) {
            this.fees = [];
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/fee/getFeesBySourceId';
            const ns = new NetworkService();
            //Type 2 is for club
            let {Result} = await ns.post$fetch(url,{
                "CompanyId": companyId,
                "SourceId": sourceId,
                "Type": 2,
            },null, "getFeesBySourceId" + new Date().getMilliseconds() + "_")
            if(withSet && (Result?.length??-1)>0){
                for(const item of Result){
                    this.fees.push(item as Fee);
                }
            }
            return Result;
        },

        // Load all payments paid by user
        loadAllPaidPaymentsOfAUser: async function (paymentsByUserIdRb:GetPaidPaymentsByUserIdRb) {
            this.paidPayments = [];
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/fee/getFeesByUserId';
            const ns = new NetworkService();
            //Type 2 is for club
            let {Result} = await ns.post$fetch(url, paymentsByUserIdRb,null, "GetPaidFeesByUserIdRb" + new Date().getMilliseconds() + "_")
            if((Result?.length??-1)>0){
                for(const item of Result){
                    this.paidPayments.push(item as PaidPayment);
                }
            }
        },

        //Get all fees by source id from server
        getAllFeesBySourceIdFromServer: async function(feesBySourceIdRb:GetFeesBySourceIdRb){
            try{
                const config = useRuntimeConfig()
                let url = '/api/fee/fees-by-sourceId';
                const ns = new NetworkService();
                let {Result} = await ns.post$fetch(url, feesBySourceIdRb, null, "getAllFeesBySourceIdFromServer" + new Date().getMilliseconds() + "_")
                if(Result){
                    return Result;
                }
            }catch (e){
                console.log(e);
            }
        },

        // Set fees for event registrations
        setFeesForEventRegistration: function (fees:Fee[]) {
            this.feesForEventRegistration = fees;
        },

        // Load fees by sourceId from server and set to local storage
        loadAndSetFeesForEventRegistrationBySourceIdFromServer: async function(feesBySourceIdRb:GetFeesBySourceIdRb){
            const fees = await this.getAllFeesBySourceIdFromServer(feesBySourceIdRb);
            if(fees){
                this.setFeesForEventRegistration((fees??[]));
                return fees;
            }
        },

        // Set selected quantity for a fee user chose
        setQuantityOfFeeForEventRegistration: function (feeId: number, quantity: number) {
            const filteredFeeIdAndQuantityList = this.feeIdAndQuantityListForEventRegistration.filter((item)=>item.feeId!=feeId);
            filteredFeeIdAndQuantityList.push(<FeeIdAndQuantity>{feeId, quantity})
            this.feeIdAndQuantityListForEventRegistration = filteredFeeIdAndQuantityList;
        },

        getSelectedFeesForEventRegistration: function () {
            const tmpFees = <Fee[]>[];
            for(const tmpFeeIdAndQuantity of this.feeIdAndQuantityListForEventRegistration){
                const tmpQuantity = tmpFeeIdAndQuantity.quantity;
                if(tmpQuantity>0){
                    const foundFee = this.feesForEventRegistration.find((fee)=>fee.id==tmpFeeIdAndQuantity.feeId);
                    if(foundFee){
                        tmpFees.push(foundFee);
                    }
                }
            }
            return tmpFees;
        },

        getQuantityByFeeIfForEventRegistration: function (feeId: any) {
            let quantity = 0;
            const foundFeeIdAndQuantity = this.feeIdAndQuantityListForEventRegistration.find((item)=>item.feeId==feeId);
            if(foundFeeIdAndQuantity){
                quantity = foundFeeIdAndQuantity.quantity;
            }
            return quantity;
        },
        //only for create event page
        setFeesForCreateEventForm: function (fee: Fee){
            this.feesForCreateEventForm = this.feesForCreateEventForm.filter((item:any) => item.id != fee.id)
            this.feesForCreateEventForm.push(fee);
        },
        setLocalClubFeeRb: function (fee: ClubFee){
            this.localClubFeeRb = this.localClubFeeRb.filter((item:any) => item.id != fee.id)
            this.localClubFeeRb.push(fee);
        },
        removeLocalClubFeeRb: function (fee: ClubFee){
            this.localClubFeeRb = this.localClubFeeRb.filter((item:any) => item.id != fee.id)
        },
        assignFeesForCreateEventForm: function (fee: any){
            this.feesForCreateEventForm = this.feesForCreateEventForm.filter((item:any) => item.id != fee.FeeId)
        },
        assignLocalClubFeeRb: function (fee: ClubFee){
            this.localClubFeeRb = this.localClubFeeRb.filter((item:any) => item.id != fee.id)
        },
        removeFeesForCreateEventForm: function (fee: any){
            this.feesForCreateEventForm = this.feesForCreateEventForm.filter((item:any) => item.id != fee.FeeId)
        },
        createUpdateFee: async function(addUpdateFeeRb: AddUpdateFeeRb){
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/fee/addUpdateFee';
            const ns = new NetworkService();
            let {Result} = await ns.post$fetch(url, addUpdateFeeRb, null, "addUpdateFeeRb" + new Date().getMilliseconds() + "_")
            return Result;
        },

        assignFee: async function(addUpdateFeeRb: AssignFeeRb){
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/fee/assignFee';
            const ns = new NetworkService();
            let {Result} = await ns.post$fetch(url, addUpdateFeeRb, null, "addUpdateFeeRb" + new Date().getMilliseconds() + "_")
            return Result;
        },
        updateCashPayment: async function(updateCashPaymentRb: updateCashPaymentRb){
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/fee/updateCashPayment';
            const ns = new NetworkService();
            let {Result} = await ns.post$fetch(url, updateCashPaymentRb, null, "addUpdateFeeRb" + new Date().getMilliseconds() + "_")
            return Result;
        },
                
        addSingleFeeToAllFeesOfCurrentCompany: function (fee:Fee)  {
            this.allFeesOfThisCompany.push(fee)
        },

        getTransactionStatistics: async function(companyId: number, from:string="", to:string="", fees:number[]=[]){
            try{
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-transaction/get-transaction-statistics';
                const ns = new NetworkService();
                let {Result} = await ns.post$fetch(url, { companyId,  fees}, null, "getTransactionStatistics" + new Date().getMilliseconds() + "_")
                // let {Result} = await ns.post$fetch(url, { companyId, from, to, fees}, null, "getTransactionStatistics" + new Date().getMilliseconds() + "_")
                if(Result){
                    return Result;
                }
            }catch (e){
                console.log(e);
            }
        },

        getTransactionsByUserId: async function(userId: number, companyId: number, withPlan: boolean=false){
            try{
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/fee/getTransactionsByUserId';
                const ns = new NetworkService();
                let {Result} = await ns.post$fetch(url, { userId, companyId, withPlan}, null, "getTransactionsByUserId" + new Date().getMilliseconds() + "_")
                if(Result){
                    return Result;
                }
            }catch (e){
                console.log(e);
            }
        },

        proceedToRefund: async function(refundRB: any){
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/fee/proceedToRefund';
            const ns = new NetworkService();
            let {Result} = await ns.post$fetch(url, refundRB, null, "refundRB" + new Date().getMilliseconds() + "_")
            return Result;
        },

        getInvoicesByCompanyId: async function(companyId: any){
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/fee/getAllInvoicesByCompanyId';
            const ns = new NetworkService();
            let {Result} = await ns.post$fetch(url, {companyId}, null, "getAllInvoicesByCompanyId" + new Date().getMilliseconds() + "_")
            return Result;
        },

        getInvoicesByStudentId: async function(studentId: any, companyId: any){
            const config = useRuntimeConfig()
            let url = config.public.baseUrlSops + '/api/fee/getAllInvoicesByStudentId';
            const ns = new NetworkService();
            let {Result} = await ns.post$fetch(url, {studentId, companyId}, null, "getAllInvoicesByStudentId" + new Date().getMilliseconds() + "_")
            return Result;
        },

        getAllFeesByCompanyId: async function(companyId: number) {
            try{
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/get-club-fees-by-company-id';
                const ns = new NetworkService();

                // Prepare the request body
                const requestBody = {
                    companyId: companyId
                };

                let {Result} = await ns.post$fetch(url, requestBody, null, "getAllFeesByCompanyId" + new Date().getMilliseconds() + "_");
                return Result;
            } catch (e){
                console.log(e);
            }
        },

        updateFee: async function(feeId: number, status?: number, name?: string) {
            try{
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/update-fee';
                const ns = new NetworkService();

                const requestBody: any = {
                    feeId
                  };
              
                  if (status !== undefined && status !== null) {
                    requestBody.status = status;
                  }
              
                  if (name !== undefined && name !== null) {
                    requestBody.name = name;
                  }
            
                let {Result} = await ns.post$fetch(url, requestBody, null, "getAllFeesByCompanyId" + new Date().getMilliseconds() + "_");
                return Result;
            } catch (e){
                console.log(e);
            }
        },

        // Get eligible students for a fee (based on enrollment type matching)
        getEligibleStudentsForFee: async function(getEligibleStudentsRb: GetEligibleStudentsRb) {
            try {
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/get-eligible-students';
                const ns = new NetworkService();
                let {Result, IsOperationSuccessful, Message} = await ns.post$fetch(
                    url,
                    getEligibleStudentsRb,
                    null,
                    "getEligibleStudentsForFee" + new Date().getMilliseconds() + "_"
                );
                return {Result, IsOperationSuccessful, Message};
            } catch (e) {
                console.log(e);
                throw e;
            }
        },

        // Connect fee to multiple students (bulk operation) and optionally create invoices
        connectFeeToStudents: async function(connectFeeToStudentsRb: ConnectFeeToStudentsRb) {
            try {
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/connect-to-students';
                const ns = new NetworkService();
                let {Result, IsOperationSuccessful, Message} = await ns.post$fetch(
                    url,
                    connectFeeToStudentsRb,
                    null,
                    "connectFeeToStudents" + new Date().getMilliseconds() + "_"
                );
                return {Result, IsOperationSuccessful, Message};
            } catch (e) {
                console.log(e);
                throw e;
            }
        },

        // Get eligible fees for a student (based on enrollment type matching)
        getEligibleFeesForStudent: async function(getEligibleFeesForStudentRb: GetEligibleFeesForStudentRb) {
            try {
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/get-eligible-fees-for-student';
                const ns = new NetworkService();
                let {Result, IsOperationSuccessful, Message} = await ns.post$fetch(
                    url,
                    getEligibleFeesForStudentRb,
                    null,
                    "getEligibleFeesForStudent" + new Date().getMilliseconds() + "_"
                );
                return {Result, IsOperationSuccessful, Message};
            } catch (e) {
                console.log(e);
                throw e;
            }
        },

        // Connect student to multiple fees and optionally create invoices
        connectStudentToFees: async function(connectStudentToFeesRb: ConnectStudentToFeesRb) {
            try {
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/connect-student-to-fees';
                const ns = new NetworkService();
                let {Result, IsOperationSuccessful, Message} = await ns.post$fetch(
                    url,
                    connectStudentToFeesRb,
                    null,
                    "connectStudentToFees" + new Date().getMilliseconds() + "_"
                );
                return {Result, IsOperationSuccessful, Message};
            } catch (e) {
                console.log(e);
                throw e;
            }
        },

        getAllFeesByStudentId: async function (studentId:number, companyId:number) {
            try{
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/get-all-club-fees-by-student-id';
                const ns = new NetworkService();
                let {Result} = await ns.post$fetch(url, {studentId, companyId}, null, "getAllFeesByStudentId" + new Date().getMilliseconds() + "_")
                return Result;
            }catch (e) {
                console.log(e)
            }
        },

        // Pause recurring fee for a student
        pauseFeeForStudent: async function (studentClubFeeMappingId: string, companyId: number) {
            try {
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/pause-fee-for-student';
                const ns = new NetworkService();
                let {Result, IsOperationSuccessful, Message} = await ns.post$fetch(
                    url,
                    { studentClubFeeMappingId, companyId },
                    null,
                    "pauseFeeForStudent" + new Date().getMilliseconds() + "_"
                );
                return {Result, IsOperationSuccessful, Message};
            } catch (e) {
                console.log(e);
                throw e;
            }
        },

        // Resume paused fee for a student
        resumeFeeForStudent: async function (studentClubFeeMappingId: string, companyId: number) {
            try {
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/resume-fee-for-student';
                const ns = new NetworkService();
                let {Result, IsOperationSuccessful, Message} = await ns.post$fetch(
                    url,
                    { studentClubFeeMappingId, companyId },
                    null,
                    "resumeFeeForStudent" + new Date().getMilliseconds() + "_"
                );
                return {Result, IsOperationSuccessful, Message};
            } catch (e) {
                console.log(e);
                throw e;
            }
        },

        // Remove fee from a student
        removeFeeFromStudent: async function (studentClubFeeMappingId: string, companyId: number) {
            try {
                const config = useRuntimeConfig()
                let url = config.public.baseUrlSops + '/api/club-fee/remove-fee-from-student';
                const ns = new NetworkService();
                let {Result, IsOperationSuccessful, Message} = await ns.post$fetch(
                    url,
                    { studentClubFeeMappingId, companyId },
                    null,
                    "removeFeeFromStudent" + new Date().getMilliseconds() + "_"
                );
                return {Result, IsOperationSuccessful, Message};
            } catch (e) {
                console.log(e);
                throw e;
            }
        },
    },
    getters: {
        getAllFeesBySourceId: state => state.fees,
        getAllPaidPayments: state => state.paidPayments,
        getAllFeesForEventRegistration: state => state.feesForEventRegistration,
        getFeeIdAndQuantityListForEventRegistration: state => state.feeIdAndQuantityListForEventRegistration,
        getTotalAmountOfSelectedFeesForEventRegistration: state => {
            let total = 0;
            for(const feeIdAndQuantity of state.feeIdAndQuantityListForEventRegistration){
                const foundFee = state.feesForEventRegistration.find((fee)=>fee.id==feeIdAndQuantity.feeId);
                const quantity = feeIdAndQuantity.quantity;
                if(foundFee){
                    total = total+ (foundFee.feeAmount*quantity);
                }
            }
            return total.toFixed(1);
        },
        getQuantityByFeeIfForEventRegistrationRef: state => function (feeId: any) {
            let quantity = 0;
            const foundFeeIdAndQuantity = state.feeIdAndQuantityListForEventRegistration.find((item)=>item.feeId==feeId);
            if(foundFeeIdAndQuantity){
                quantity = foundFeeIdAndQuantity.quantity;
            }
            return quantity;
        },
        getTotalSelectedFeeQuantityForEventRegistration: state => {
            let total = 0;
            for(const feeIdQuantity of state.feeIdAndQuantityListForEventRegistration){
                total = total + (feeIdQuantity.quantity??0);
            }
            return total;
        },
        // only for create event page
        getFeesForCreateEventForm: state => state.feesForCreateEventForm,
        getFeesFromLocalClubFee: state => state.localClubFeeRb,
        getFeeByIdFromEventRegistrationFees: state => function (feeId:number) {
            return state.feesForEventRegistration.find((fee)=>fee.id==feeId);
        },
        getFeeById: state=> function (feeId:number) {
            return state.allFeesOfThisCompany.find((item) => item.id == feeId)
        },
        getFeeNameById: state=> function (feeId:number) {
            let fee = state.allFeesOfThisCompany.find((item) => item.id == feeId);
            return fee?.title ?? ''
        },
        getFeeAmountById: state=> function (feeId:number) {
            let fee = state.allFeesOfThisCompany.find((item) => item.id == feeId);
            return fee?.feeAmount ?? ''
        },
        getActiveFeesOfCompany: state=> state.allFeesOfThisCompany.filter((item:any) => item.status == 1),
        getAllCompanyFees: state =>  state.allFeesOfThisCompany,
        getAllClubFees: state =>  state.allFeesOfThisClub
    },
})